// Libs
import { TFunction } from 'i18next';
import * as yup from 'yup';
// Others
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { JobSourceTypeEnum } from '~/utils/enum/common';
import { dataTimeZone } from '~/utils/helper/common';
import {
  IChecklistEstimate,
  IDiscountEstimate,
  IFormEstimate,
  IMaterialsEstimate,
  IServiceEstimate,
} from '~/utils/interface/estimate';

export const schema = (t: TFunction) => {
  return yup.object().shape({
    clientId: yup
      .string()
      .trim()
      .required(t('common_validate_field_required', { ns: 'common/base' })),
    source: yup.string().trim().optional(),
    referralId: yup
      .string()
      .trim()
      .when('source', {
        is: (value: string) => value === JobSourceTypeEnum.REFERRAL,
        then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
        otherwise: (schema) => schema.optional(),
      }),
    referralType: yup.string().trim().optional(),
    tags: yup.string().trim().optional(),
    type: yup.string().trim().optional(),
    clientAddressId: yup.string().trim().optional(),
    unit: yup.string().trim().optional(),
    projectedServices: yup
      .array()
      .of(
        yup.object().shape({
          serviceId: yup
            .string()
            .trim()
            .test(
              'serviceId-required',
              t('common_validate_field_required', { ns: 'common/base' }),
              function (value) {
                const { serviceId } = this.parent;
                if (serviceId && serviceId.trim() !== '') {
                  if (!value || value.trim() === '') {
                    return this.createError({
                      message: t('common_validate_field_required', { ns: 'common/base' }),
                    });
                  }
                }
                return true;
              }
            ),
          price: yup
            .string()
            .trim()
            .when('serviceId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          cost: yup
            .string()
            .trim()
            .when('serviceId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          quantity: yup
            .string()
            .trim()
            .when('serviceId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          description: yup.string().trim().optional(),
        })
      )
      .optional(),
    projectedMaterials: yup
      .array()
      .of(
        yup.object().shape({
          materialId: yup
            .string()
            .trim()
            .test(
              'materialId-required',
              t('common_validate_field_required', { ns: 'common/base' }),
              function (value) {
                const { materialId } = this.parent;
                if (materialId && materialId.trim() !== '') {
                  if (!value || value.trim() === '') {
                    return this.createError({
                      message: t('common_validate_field_required', { ns: 'common/base' }),
                    });
                  }
                }
                return true;
              }
            ),
          price: yup
            .string()
            .trim()
            .when('materialId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          cost: yup
            .string()
            .trim()
            .when('materialId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          quantity: yup
            .string()
            .trim()
            .when('materialId', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
          description: yup.string().trim().optional(),
        })
      )
      .optional(),
    discounts: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            type: yup
              .string()
              .trim()
              .test(
                'type-required',
                t('common_validate_field_required', { ns: 'common/base' }),
                function (value) {
                  const { type } = this.parent;
                  if (type && type.trim() !== '') {
                    if (!value || value.trim() === '') {
                      return this.createError({
                        message: t('common_validate_field_required', { ns: 'common/base' }),
                      });
                    }
                  }
                  return true;
                }
              ),
            rate: yup.string().when('type', {
              is: (value: string) => value && value.trim() !== '',
              then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
              otherwise: (schema) => schema.optional(),
            }),
            description: yup.string().trim().optional(),
          })
          .test(
            'require-all-if-any',
            t('common_validate_field_required', { ns: 'common/base' }),
            function (value) {
              const { type, rate } = value || {};
              const hasAny = !!(type || rate);
              const allFilled = !!(type && rate);
              if (hasAny && !allFilled) {
                return this.createError({
                  message: t('common_validate_field_required', { ns: 'common/base' }),
                });
              }
              return true;
            }
          )
      )
      .optional(),
    // checklists: yup.array().of(
    //   yup.object().shape({
    //     name: yup
    //       .string()
    //       .trim()
    //       .required(t('common_validate_field_required', { ns: 'common/base' })),
    //     type: yup
    //       .string()
    //       .trim()
    //       .required(t('common_validate_field_required', { ns: 'common/base' })),
    //   })
    // ),
    total: yup.number().optional(),
    address: requiredIfClientId(t),
    address2: yup.string().trim().optional(),
    city: requiredIfClientId(t),
    state: requiredIfClientId(t),
    zipCode: requiredIfClientId(t),
    country: requiredIfClientId(t),
    assigneeIds: yup
      .array()
      .of(yup.string().required(t('common_validate_field_required', { ns: 'common/base' })))
      .optional(),
  });
};

const requiredIfClientId = (t: TFunction) =>
  yup
    .string()
    .trim()
    .when('clientId', {
      is: (val: string) => !!val,
      then: (schema) => schema.required(t('common_validate_field_required', { ns: 'common/base' })),
      otherwise: (schema) => schema.optional(),
    });

export const defaultValuesForm: IFormEstimate = {
  assigneeIds: [],
  source: EMPTY_STRING,
  tags: EMPTY_STRING,
  clientId: EMPTY_STRING,
  type: EMPTY_STRING,
  referralId: EMPTY_STRING,
  referralType: EMPTY_STRING,
  address: EMPTY_STRING,
  address2: EMPTY_STRING,
  city: EMPTY_STRING,
  state: EMPTY_STRING,
  zipCode: EMPTY_STRING,
  country: EMPTY_STRING,
  subtotal: DEFAULT_NUMBER_ZERO,
  total: DEFAULT_NUMBER_ZERO,
  callback: false,
  endDate: EMPTY_STRING,
  endTime: EMPTY_STRING,
  startDate: EMPTY_STRING,
  startTime: EMPTY_STRING,
  timezone: dataTimeZone(),
  folders: [],
  tagIds: [],
  leadSourceId: EMPTY_STRING,
  unit: EMPTY_STRING,
};

export const defaultValuesFormService: IServiceEstimate = {
  serviceId: EMPTY_STRING,
  price: EMPTY_STRING,
  cost: EMPTY_STRING,
  quantity: EMPTY_STRING,
  description: EMPTY_STRING,
};

export const defaultValuesFormMaterial: IMaterialsEstimate = {
  materialId: EMPTY_STRING,
  price: EMPTY_STRING,
  cost: EMPTY_STRING,
  quantity: EMPTY_STRING,
  description: EMPTY_STRING,
};

export const defaultValuesFormDiscount: IDiscountEstimate = {
  type: EMPTY_STRING,
  rate: EMPTY_STRING,
  description: EMPTY_STRING,
};

export const defaultValuesFormChecklist: IChecklistEstimate = {
  type: EMPTY_STRING,
  name: EMPTY_STRING,
};
